/**
 * Choosing WHICH Checkout a draft submits against.
 *
 * Extracted from `NewThreadLanding.logic` because the decision is substantive
 * enough to own a file: one Environment hosts every Checkout its owner has
 * registered, and getting this wrong dispatches a turn against a different
 * project than the person chose.
 */
import type { CmmdRegistration } from "@cmmd-forge/contracts";
import type { CloudProjectCatalogRow } from "../../cmmd/cmmdForgeRuntimeListResource";
import { classifyEnvironmentId } from "../../environments/environmentIdClassification";
import {
  parseCloudProjectKey,
  resolveCloudProjectEnvironmentId,
} from "../sidebar/cloudProjectsGroup.logic";
import {
  cloudRowWorkspaceDirectoryName,
  directoryNameOf,
  namedWorkspaceRoot,
  normalizePath,
} from "../sidebar/cloudRuntimeRowState.logic";

/**
 * The Registration fields every catalog row carries, plus its Environment and
 * the identity each server route derives the Checkout directory from.
 */
type CheckoutRow = Pick<
  CloudProjectCatalogRow,
  "environmentId" | "cmmdOrganizationId" | "cmmdProjectId"
> &
  Partial<Pick<CloudProjectCatalogRow, "githubRepoFullName" | "projectName" | "workspaceRoot">>;

/**
 * Does this row name the directory a Checkout lives in? The same two readings
 * adoption uses (residentsByDirectory): the directory CMMD seeded and recorded
 * as the row's workspaceRoot, then the clone convention derived from the
 * repository or the project name. Case-insensitive on the name, because GitHub
 * repository names are and a Sprite seeds CMMD-Center/CMMD at /projects/cmmd.
 */
function rowNamesDirectory(row: CheckoutRow, cwd: string): boolean {
  const seeded = namedWorkspaceRoot(row.workspaceRoot ?? null);
  if (seeded !== null && normalizePath(cwd) === seeded) return true;
  if (row.projectName === undefined) return false;
  const conventional = cloudRowWorkspaceDirectoryName({
    cmmdOrganizationId: row.cmmdOrganizationId,
    cmmdProjectId: row.cmmdProjectId,
    githubRepoFullName: row.githubRepoFullName ?? null,
    projectName: row.projectName,
    workspaceRoot: row.workspaceRoot ?? null,
  });
  return directoryNameOf(cwd)?.toLowerCase() === conventional.toLowerCase();
}

/** What the draft knows about the Checkout it resolved, if it resolved one. */
export interface ResolvedCheckout {
  readonly cmmdRegistration?: CmmdRegistration | null;
  readonly cwd?: string;
}

function isCloudRuntimeEnvironmentId(environmentId: string | null | undefined): boolean {
  return environmentId ? classifyEnvironmentId(environmentId).isCloud : false;
}

/**
 * The Checkout row a Registration names on one Environment.
 *
 * THE resolver. A Registration (organization plus CMMD project) is the durable
 * identity of a Checkout on an Environment, and it is the key this lookup uses.
 * A Forge Cloud Environment is per (organization, user), so it holds every
 * Checkout its owner registered, every one of them reports the sprite's own
 * root as its workspace root, and a connected one reports an empty `cwd`.
 * Nothing about a path can tell them apart, which is why no path appears here.
 *
 * A Registration that names no row on this Environment returns null. It never
 * falls back to the only row, because a Registration the Environment does not
 * carry belongs to a different project and running the turn against the row
 * that happens to be here would dispatch it somewhere the person did not
 * choose.
 *
 * `registration` is null only when the selection cannot name one at all: a
 * Checkout connected before Forge stamped Registrations onto project records.
 * For that Checkout a directory breaks the tie, exactly as the glossary
 * allows: both server routes derive the Checkout directory from the row, so
 * the row can derive it back and the last segment of the Checkout's `cwd`
 * names it. That match is what lets the draft run the connect which stamps
 * the Registration; without it the row stayed null on every Environment with
 * more than one Checkout and the record was never stamped (FOR-36402). A
 * lone row is still returned when nothing else can name it, which is what
 * keeps that project's runtime-ticket refresh working.
 */
export function resolveCheckoutRowForRegistration<T extends CheckoutRow>(
  rows: readonly T[],
  environmentId: string | null,
  registration: CmmdRegistration | null,
  checkoutCwd: string | null = null,
): T | null {
  if (!isCloudRuntimeEnvironmentId(environmentId)) return null;
  const onEnvironment = rows.filter(
    (row) => resolveCloudProjectEnvironmentId(row) === environmentId,
  );
  if (registration) {
    return (
      onEnvironment.find(
        (row) =>
          row.cmmdOrganizationId === registration.cmmdOrganizationId &&
          row.cmmdProjectId === registration.cmmdProjectId,
      ) ?? null
    );
  }
  if (checkoutCwd) {
    const byDirectory = onEnvironment.filter((row) => rowNamesDirectory(row, checkoutCwd));
    if (byDirectory.length === 1) return byDirectory[0] ?? null;
  }
  return onEnvironment.length === 1 ? (onEnvironment[0] ?? null) : null;
}

/**
 * The Checkout row for the project a draft currently has selected.
 *
 * Resolves the selection to a Registration and hands it to the one resolver.
 * A connected Checkout carries its Registration on its record; one the person
 * picked before it connected is named by its selection key, which is that same
 * Registration written out.
 */
export function selectCloudCatalogRowForProject<T extends CheckoutRow>(
  rows: readonly T[],
  selection: { readonly key: string | null; readonly environmentId: string | null },
  checkout: ResolvedCheckout | null,
): T | null {
  return resolveCheckoutRowForRegistration(
    rows,
    selection.environmentId,
    checkout?.cmmdRegistration ?? parseCloudProjectKey(selection.key),
    checkout?.cwd ?? null,
  );
}
